// src/pages/AdminDashboard.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaPlus, FaList, FaUsers, FaSignOutAlt, FaTachometerAlt, 
  FaEdit, FaUserCircle, FaRegBell, FaSearch, FaCog, FaBell 
} from 'react-icons/fa';
import AddNewEvent from '../components/AddNewEvent';
import ExistingEvents from '../components/ExistingEvents';
import ViewRegistrations from '../components/ViewRegistrations';
import Dashboard from '../components/Dashboard';
import NotificationManager from '../components/NotificationManager';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import EditEventPage from '../pages/EditEventPage';

/**
 * AdminDashboard Component
 * 
 * Main dashboard for admin users with navigation to different admin functions
 * 
 * @param {Object} user - The authenticated user object from Firebase Auth
 * @returns {JSX.Element} - Rendered admin dashboard with navigation and content
 */
const AdminDashboard = ({ user }) => {
  // State management for UI and navigation
  const [activeTab, setActiveTab] = useState('dashboard'); // Current active tab
  const [modal, setModal] = useState({ isVisible: false, title: "", message: "", onConfirm: null }); // Modal state
  const [editEventId, setEditEventId] = useState(null); // ID of event being edited
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Control sidebar collapse state
  const navigate = useNavigate(); // React Router navigation hook

  /**
   * Shows confirmation modal before signing out
   */
  const handleSignOut = () => {
    setModal({
      isVisible: true,
      title: 'Confirm Sign Out',
      message: 'Are you sure you want to sign out?',
      onConfirm: confirmSignOut
    });
  };

  /**
   * Performs the actual sign out operation after confirmation
   */
  const confirmSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      setModal({
        isVisible: true,
        title: 'Error',
        message: 'Failed to sign out. Please try again.',
        onConfirm: null
      });
    }
  };

  /**
   * Sets the event ID to edit and changes to edit tab
   * @param {string} eventId - ID of the event to edit
   */
  const handleEditEvent = (eventId) => {
    setEditEventId(eventId);
    setActiveTab('edit');
  };

  /**
   * Handles returning from edit page with success feedback
   */
  const handleEditComplete = () => {
    setActiveTab('events');
    setEditEventId(null);
    // Show success notification
    setModal({
      isVisible: true,
      title: 'Success',
      message: 'Event updated successfully! Changes are now live.',
      onConfirm: null
    });
  };

  /**
   * Renders the main content based on the active tab
   * @returns {JSX.Element} - The component for the active tab
   */
  const renderMainContent = () => {
    if (activeTab === 'edit' && editEventId) {
      return (
        <EditEventPage 
          eventId={editEventId} 
          onClose={handleEditComplete} 
          user={user} 
        />
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return <Dashboard user={user} />;
      case 'events':
        return (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-semibold mb-6">Existing Events</h2>
            <ExistingEvents onEditEvent={handleEditEvent} user={user} />
          </div>
        );
      case 'new':
        return (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-semibold mb-6">Add New Event</h2>
            <AddNewEvent 
              user={user} 
              onEventAdded={() => {
                setActiveTab('events');
                // Show success notification
                setModal({
                  isVisible: true,
                  title: 'Success',
                  message: 'Event created successfully! You can now view it in the Events tab.',
                  onConfirm: null
                });
              }}
            />
          </div>
        );
      case 'edit':
        return (
          // Display ExistingEvents to choose which event to edit
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold mb-2">Edit Event</h2>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>How to edit an event:</strong> Click the <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-indigo-100 text-indigo-700 mx-1">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.828-2.828z" />
                        </svg>
                        Edit
                      </span> button next to the event you want to modify.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <ExistingEvents onEditEvent={handleEditEvent} user={user} />
          </div>
        );
      case 'registrations':
        return (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-semibold mb-6">View Registrations</h2>
            <ViewRegistrations user={user} />
          </div>
        );
      case 'notifications':
        return (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <NotificationManager user={user} />
          </div>
        );
      default:
        return null;
    }
  };

  // Display welcome message with user email if available
  const userEmail = user?.email || "Admin";
  const userDisplayName = user?.displayName || userEmail.split('@')[0];

  // Navigation items for sidebar
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: FaTachometerAlt },
    { id: 'events', label: 'Events', icon: FaList },
    { id: 'new', label: 'Add New Event', icon: FaPlus },
    { id: 'edit', label: 'Edit Event', icon: FaEdit },
    { id: 'registrations', label: 'Registrations', icon: FaUsers },
    { id: 'notifications', label: 'Notifications', icon: FaBell }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Fixed Sidebar */}
      <div 
        className={`bg-white shadow-lg fixed h-full transition-all duration-300 z-20 ${
          sidebarCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo and Brand */}
          <div className={`p-6 ${sidebarCollapsed ? 'items-center' : ''}`}>
            <div className="flex items-center">
              <div className="bg-indigo-600 text-white p-2 rounded-lg">
                <FaTachometerAlt size={sidebarCollapsed ? 20 : 18} />
              </div>
              {!sidebarCollapsed && (
                <div className="ml-3">
                  <h1 className="font-bold text-lg text-gray-900">YouthXConnect</h1>
                  <p className="text-xs text-gray-500">Events Admin</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Collapse Toggle Button */}
          <button 
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute -right-3 top-20 bg-white border border-gray-200 rounded-full p-1 shadow-md"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${sidebarCollapsed ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          {/* Navigation Menu */}
          <nav className="mt-6 flex-grow">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-start'} px-6 py-4 text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors ${
                    activeTab === item.id ? 'bg-indigo-50 text-indigo-600 border-r-4 border-indigo-600' : ''
                  }`}
                >
                  <Icon className={sidebarCollapsed ? 'mx-auto' : 'mr-3'} size={18} />
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </button>
              );
            })}
          </nav>
          
          {/* User Profile and Sign Out */}
          <div className={`mt-auto border-t border-gray-100 ${sidebarCollapsed ? 'p-4' : 'p-6'}`}>
            {!sidebarCollapsed && (
              <div className="flex items-center mb-4">
                <div className="bg-gray-100 p-2 rounded-full">
                  <FaUserCircle className="text-gray-500" size={24} />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">{userDisplayName}</p>
                  <p className="text-xs text-gray-500 truncate max-w-[150px]">{userEmail}</p>
                </div>
              </div>
            )}
            <button
              onClick={handleSignOut}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-start'} px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors`}
            >
              <FaSignOutAlt className={sidebarCollapsed ? '' : 'mr-2'} />
              {!sidebarCollapsed && <span>Sign Out</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div 
        className={`flex-1 transition-all duration-300 ${
          sidebarCollapsed ? 'ml-20' : 'ml-64'
        }`}
      >
        {/* Top Header Bar */}
        <header className="bg-white shadow-sm h-16 fixed w-full z-10" style={{ width: `calc(100% - ${sidebarCollapsed ? '5rem' : '16rem'})` }}>
          <div className="flex items-center justify-between h-full px-6">
            {/* Page Title */}
            <h1 className="text-xl font-semibold text-gray-800">
              {navItems.find(item => item.id === activeTab)?.label || 'Dashboard'}
            </h1>
            
            {/* Header Actions */}
            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative hidden md:block">
                <input
                  type="text"
                  placeholder="Search..."
                  className="bg-gray-100 rounded-lg py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white w-48 lg:w-64 transition-all"
                />
                <FaSearch className="absolute left-3 top-2.5 text-gray-400" size={16} />
              </div>
              
              {/* Notification Bell */}
              <button className="p-2 rounded-full hover:bg-gray-100 transition-colors relative">
                <FaRegBell className="text-gray-600" size={18} />
                <span className="absolute top-1 right-1 bg-red-500 rounded-full w-2 h-2"></span>
              </button>
              
              {/* Settings */}
              <button className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                <FaCog className="text-gray-600" size={18} />
              </button>
              
              {/* Mobile User Profile */}
              <div className="md:hidden">
                <FaUserCircle className="text-gray-600" size={24} />
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="pt-24 px-8 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-7xl mx-auto"
          >
            {renderMainContent()}
          </motion.div>
        </main>
      </div>

      {/* Confirmation Modal */}
      <Modal
        isVisible={modal.isVisible}
        title={modal.title}
        message={modal.message}
        onClose={() => setModal({ isVisible: false, title: "", message: "", onConfirm: null })}
        onConfirm={modal.onConfirm}
      />
    </div>
  );
};

export default AdminDashboard;